import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion, prefersReducedMotion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { KlarnaPopupYield } from '@theme/klarna-popup-yield';

let latestOpenRequest = 0;

/**
 * The card whose product the shared modal is showing.
 * @type {QuickAddComponent | null}
 */
let activeQuickAdd = null;

/**
 * Desktop = 1024px+ with a mouse (the size row opens on hover); tablets get the mobile
 * flyout. Keep in step with the media queries in snippets/quick-add.liquid.
 */
const quickAddDesktopQuery = matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');

export function isQuickAddMobile() {
  return !quickAddDesktopQuery.matches;
}

const MODAL_HEIGHT_STORAGE_KEY = 'quick-add-modal-height';

const TYPICAL_MODAL_HEIGHT = 450;

/** Delay before a colour swap's swatch shows a spinner. */
const COLOUR_SWAP_SPINNER_DELAY = 300;

function getLastLoadedModalHeight() {
  try {
    return Number(localStorage.getItem(MODAL_HEIGHT_STORAGE_KEY)) || TYPICAL_MODAL_HEIGHT;
  } catch {
    return TYPICAL_MODAL_HEIGHT;
  }
}

/** @param {number} height */
function setLastLoadedModalHeight(height) {
  try {
    localStorage.setItem(MODAL_HEIGHT_STORAGE_KEY, String(Math.round(height)));
  } catch {
    // Storage blocked or full: the placeholder just opens at its own height.
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {AbortController} */
  #inlineSizesAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    // No card (`openQuickAddModal`, Notify Me's similar items): the URL comes from the component itself.
    const href = productLink?.href || this.dataset.productUrl;

    if (!href) return '';

    const url = new URL(href, window.location.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Recreated rather than reused: disconnectedCallback aborts it, and a card that is
    // moved rather than replaced (morph adopting the node) reconnects with the same
    // instance — an already-aborted controller would drop the reset listeners silently.
    this.#inlineSizesAbortController = new AbortController();

    quickAddDesktopQuery.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    quickAddDesktopQuery.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#inlineSizesAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Reveals the PLP card's inline size row in place of its "+" button.
   * @param {Event} event
   */
  openInlineSizes = (event) => {
    event.preventDefault();

    this.dataset.sizesOpen = 'true';
    this.querySelector('.quick-add__sizes-toggle')?.setAttribute('aria-expanded', 'true');

    // The "+" is display:none once the row is open, so focus has to move with it — both so
    // a keyboard shopper keeps their place, and because the row's own `:focus-within`
    // condition is what holds it open for them when the pointer isn't on the card.
    const firstSize = this.querySelector('.quick-add__size');
    if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });

    // Leaving the card returns it to its default state, so the next hover offers the "+"
    // again rather than a size row the shopper never reopened. Registered lazily (a PLP
    // renders dozens of these) and deduped by the browser on re-open, since both listeners
    // are stable instance properties.
    const card = this.closest('product-card');
    const { signal } = this.#inlineSizesAbortController;
    card?.addEventListener('pointerleave', this.closeInlineSizes, { signal });
    card?.addEventListener('focusout', this.#handleInlineSizesFocusOut, { signal });
    card?.addEventListener('keydown', this.#handleInlineSizesKeydown, { signal });

    const sizes = this.querySelector('.quick-add__sizes');
    if (sizes instanceof HTMLElement) {
      sizes.addEventListener('wheel', this.#scrollSizesWithWheel, { signal, passive: false });
    }
  };

  /**
   * Scrolls an overflowing size row sideways with a vertical mouse wheel, which otherwise can't reach it.
   * @param {WheelEvent} event
   */
  #scrollSizesWithWheel = (event) => {
    const sizes = event.currentTarget;
    if (!(sizes instanceof HTMLElement)) return;

    // Trackpads already scroll sideways on their own.
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    if (sizes.scrollWidth <= sizes.clientWidth) return;

    event.preventDefault();
    sizes.scrollLeft += event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY;
  };

  /**
   * Lets a keyboard shopper dismiss the inline size row with Escape, the same as its close button.
   * @param {KeyboardEvent} event
   */
  #handleInlineSizesKeydown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || !this.dataset.sizesOpen) return;

    event.preventDefault();
    this.closeInlineSizes(event);
  };

  /**
   * Returns the card to its "+" state. Also used as a listener, so it takes any event.
   * @param {Event} [event]
   */
  closeInlineSizes = (event) => {
    if (!this.dataset.sizesOpen) return;

    delete this.dataset.sizesOpen;

    const toggle = this.querySelector('.quick-add__sizes-toggle');
    toggle?.setAttribute('aria-expanded', 'false');

    // Only when the shopper dismissed the row themselves: focus was inside the row that
    // just went away, so it has to land somewhere deliberate. Closing because the pointer
    // left the card must not pull focus across the page.
    if ((event?.type === 'click' || event?.type === 'keydown') && toggle instanceof HTMLElement) {
      toggle.focus({ preventScroll: true });
    }
  };

  /**
   * Closes the inline size row once focus moves off the card to somewhere else on the page,
   * so the next hover offers the "+" rather than a row the shopper tabbed away from.
   * @param {FocusEvent} event
   */
  #handleInlineSizesFocusOut = (event) => {
    const next = event.relatedTarget;

    // A null relatedTarget is focus falling back to the document, not the shopper moving on
    // — and it is what the browser reports when the element that had focus is the one that
    // just went away (the "+" hiding itself on open, the row hiding on close). Resetting on
    // it would close the row in the same tick the click opened it. `pointerleave` still
    // covers the shopper genuinely leaving the card.
    if (!(next instanceof Node)) return;
    if (this.closest('product-card')?.contains(next)) return;

    this.closeInlineSizes();
  };

  /**
   * Opens the page-level Notify Me drawer for a struck-through size, filled in for this
   * card's product, so the shopper subscribes without leaving the grid. Falls through to
   * the link's own `?notify=1` PDP href whenever the drawer can't take over.
   * @param {Event} event
   */
  openNotify = (event) => {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;

    // A modified click is the shopper asking for a new tab/window - give them the PDP.
    const { metaKey, ctrlKey, shiftKey, altKey } = /** @type {MouseEvent} */ (event);
    if (metaKey || ctrlKey || shiftKey || altKey) return;

    const drawer = /** @type {(Element & { openFor?: Function }) | null} */ (
      document.querySelector('back-in-stock-component[data-shared]')
    );
    if (typeof drawer?.openFor !== 'function') return;

    event.preventDefault();

    drawer.openFor({
      productId: link.dataset.notifyProductId ?? '',
      variantId: link.dataset.notifyVariantId ?? '',
      size: link.dataset.notifySize ?? '',
      ...this.#notifyContext(link.href),
    });
  };

  /**
   * What names this card's product in the Notify Me drawer, which opens away from the PDP
   * and so has nothing else on screen to identify it. All three come from what the card
   * already carries rather than from new per-card attributes: the title from this
   * component (the quick-add modal's own), the thumbnail from the card's first gallery
   * image - already decoded, so it paints with no request - and the link from whichever
   * PDP url is in hand, minus the param that would pop the drawer again on arrival.
   *
   * @param {string} [href] - The PDP url to link to. Defaults to this card's own.
   * @returns {{ title: string, image: string, url: string }}
   */
  #notifyContext(href) {
    const cardImage = this.closest('product-card')?.querySelector('.product-media__image');
    let url = href || this.productPageUrl;

    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.delete('notify');
      url = parsed.toString();
    } catch {
      // A card without a resolvable link just gets no link; the title still names it.
      url = '';
    }

    return {
      title: this.dataset.productTitle ?? '',
      image:
        cardImage instanceof HTMLImageElement ? cardImage.currentSrc || cardImage.src : this.dataset.productImage ?? '',
      url,
    };
  }

  /**
   * Hands the quick-add modal this card's product context. The modal shows the *PDP's own*
   * back-in-stock component, rescued out of the fetched page with no card around it, and on
   * mobile its drawer covers the modal completely - so that component reads this back when
   * it opens (`assets/back-in-stock.js`) rather than scraping the modal's markup, which
   * differs between breakpoints. Written before the content is morphed in, so it is already
   * there by the time the component connects.
   */
  #stashNotifyContext() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof HTMLElement)) return;

    const { title, image, url } = this.#notifyContext();
    dialogComponent.dataset.notifyTitle = title;
    dialogComponent.dataset.notifyImage = image;
    dialogComponent.dataset.notifyUrl = url;
  }

  /**
   * Selects the exact size variant before the product form submits.
   * @param {Event} event
   */
  selectInlineVariant(event) {
    const button = event.target;
    // `variantId` lives inside the nested <product-form-component>, so it belongs to that
    // component's refs, not this one's - query for it directly instead of via `this.refs`.
    const variantId = this.querySelector('input[name="id"]');
    if (!(button instanceof HTMLButtonElement) || !(variantId instanceof HTMLInputElement)) return;

    variantId.value = button.dataset.variantId || '';

    // This button submits the form directly and never goes through the add-to-cart
    // button's own click handler, so trigger the same feedback here - and skip it on the
    // same condition that handler uses, since the drawer opening is the feedback when it
    // does open and the button's checkmark burst would only fight the pending spinner.
    const productForm = /** @type {HTMLElement | null} */ (this.querySelector('product-form-component'));
    if (productForm?.dataset.openCartDrawerOnAdd === 'true') return;

    const addToCartComponent = /** @type {import('./product-form').AddToCartComponent | null} */ (
      this.querySelector('add-to-cart-component')
    );
    addToCartComponent?.triggerAnimation();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    activeQuickAdd = this;
    this.#stashNotifyContext();

    const currentUrl = this.productPageUrl;
    const modalContent = document.getElementById('quick-add-modal-content');

    // Every card shares one modal, so a slow response for a card the shopper has since moved
    // on from must not land in it. Only the latest open gets to fill the modal.
    const request = ++latestOpenRequest;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    /** The modal's height just before the content replaces the placeholder, if it showed. */
    let placeholderHeight = 0;

    if (!productGrid) {
      // Open now and fill in when the product page arrives, rather than leaving the tap
      // with no visible effect for as long as the fetch takes. The placeholder also clears
      // out the previous product's content, which would otherwise show in the meantime.
      this.#showLoadingState(modalContent);
      this.#openQuickAddModal();

      /** @type {Document | null} */
      let html;
      try {
        html = await this.fetchProductPage(currentUrl);
      } catch (error) {
        console.error(error);
        if (request !== latestOpenRequest) return;
        // Nothing to show in the modal; send the shopper to the product page instead.
        this.#closeQuickAddModal();
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      const gridElement = html?.querySelector('[data-product-grid-content]');
      if (gridElement) {
        // Cache the cloned element to avoid modifying the original
        productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(currentUrl, productGrid);
      }

      // Superseded by a later open, or closed before the content arrived.
      if (request !== latestOpenRequest || !this.#isQuickAddModalOpen()) return;

      if (!productGrid) {
        // `null` html means this fetch was aborted by a newer one, which owns the modal now.
        if (!html) return;
        this.#closeQuickAddModal();
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      placeholderHeight = this.#pinModalHeight();
    } else {
      // A placeholder for a fetch that was closed before it landed can leave a height behind.
      this.#getModalDialog()?.style.removeProperty('height');
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);
    modalContent?.removeAttribute('aria-busy');

    if (placeholderHeight) this.#settleModalHeight(placeholderHeight);

    if (!this.#isQuickAddModalOpen()) this.#openQuickAddModal();
  };

  /**
   * Loads another colour (a separate product) into the open modal instead of navigating.
   * @param {string} url - The other colour's product URL.
   * @param {HTMLElement} [swatch] - The tapped swatch, which spins if the fetch runs long.
   */
  async swapColour(url, swatch) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const request = ++latestOpenRequest;
    const productGrid = this.#cachedContent.get(url);

    const spinnerTimer = setTimeout(() => swatch?.setAttribute('aria-busy', 'true'), COLOUR_SWAP_SPINNER_DELAY);
    try {
      await this.#loadColour(url, request, modalContent, productGrid);
    } finally {
      clearTimeout(spinnerTimer);
      swatch?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} url
   * @param {number} request - This swap's `latestOpenRequest` ticket.
   * @param {HTMLElement} modalContent
   * @param {Element | undefined} productGrid - The colour's cached content, if any.
   */
  async #loadColour(url, request, modalContent, productGrid) {
    if (!productGrid) {
      modalContent.setAttribute('aria-busy', 'true');

      /** @type {Document | null} */
      let html;
      try {
        html = await this.fetchProductPage(url);
      } catch (error) {
        console.error(error);
        if (request === latestOpenRequest) window.location.href = url;
        return;
      }

      // `null` html: aborted by a newer fetch.
      if (!html || request !== latestOpenRequest) return;

      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) {
        window.location.href = url;
        return;
      }

      productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(url, productGrid);
    }

    if (request !== latestOpenRequest || !this.#isQuickAddModalOpen()) return;

    const fromHeight = this.#pinModalHeight();
    await this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)), url);
    this.#updateVariantPicker(productGrid);
    modalContent.removeAttribute('aria-busy');
    this.#stashSwappedNotifyContext(productGrid, url);

    if (fromHeight) this.#settleModalHeight(fromHeight);
  }

  /**
   * Points the modal's Notify Me image and link at the swapped-in colour.
   * @param {Element} productGrid
   * @param {string} url
   */
  #stashSwappedNotifyContext(productGrid, url) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof HTMLElement)) return;

    const image = productGrid.querySelector('.product-information__media img');
    if (image instanceof HTMLImageElement) dialogComponent.dataset.notifyImage = image.src;
    dialogComponent.dataset.notifyUrl = this.#notifyContext(url).url;
  }

  /**
   * Swaps the modal's content for a placeholder while the product page is fetched.
   * @param {HTMLElement | null} modalContent
   */
  #showLoadingState(modalContent) {
    if (!modalContent) return;

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';

    const title = document.createElement('p');
    title.className = 'quick-add-modal__loading-title';
    title.textContent = this.dataset.productTitle || '';

    const spinner = document.createElement('span');
    spinner.className = 'quick-add-modal__loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    loading.append(title, spinner);
    modalContent.replaceChildren(loading);
    modalContent.setAttribute('aria-busy', 'true');

    // The mobile modal is sized by its content, so a placeholder at its own small height
    // would jump once the product arrives. Open at the height the last product came out at
    // instead; `#settleModalHeight` covers whatever difference is left.
    const dialog = this.#getModalDialog();
    const lastLoadedModalHeight = getLastLoadedModalHeight();
    if (dialog && isQuickAddMobile() && lastLoadedModalHeight) {
      dialog.style.height = `${lastLoadedModalHeight}px`;
    } else {
      dialog?.style.removeProperty('height');
    }
  }

  /**
   * Fixes the mobile modal at its current height, so that swapping the content in doesn't
   * resize it before `#settleModalHeight` can animate the change.
   * @returns {number} The pinned height, or 0 when there is nothing to animate.
   */
  #pinModalHeight() {
    const dialog = this.#getModalDialog();
    if (!dialog || !isQuickAddMobile()) return 0;

    const height = dialog.getBoundingClientRect().height;
    dialog.style.height = `${height}px`;
    return height;
  }

  /**
   * Releases the pinned height and animates from it to the height the content needs.
   * @param {number} fromHeight
   */
  #settleModalHeight(fromHeight) {
    const dialog = this.#getModalDialog();
    if (!dialog) return;

    dialog.style.removeProperty('height');
    const toHeight = dialog.getBoundingClientRect().height;
    setLastLoadedModalHeight(toHeight);

    if (Math.abs(toHeight - fromHeight) < 1 || prefersReducedMotion()) return;

    dialog.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], {
      duration: 250,
      easing: 'cubic-bezier(0.33, 1, 0.68, 1)', // --ease-out-cubic
    });
  }

  #getModalDialog() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return null;
    return dialogComponent.refs?.dialog ?? null;
  }

  #isQuickAddModalOpen() {
    return Boolean(this.#getModalDialog()?.open);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {string} [productUrl] - The product the grid belongs to. Defaults to this card's.
   */
  async updateQuickAddModal(productGrid, productUrl = this.productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Merchant HTML can carry an unscoped <style> that would leak onto the host page.
    for (const style of productGrid.querySelectorAll('.product-disclosure-flyout__text style')) {
      style.remove();
    }

    if (isQuickAddMobile()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      // The colour family swatches, product disclosures (Product Details/Care/etc.)
      // and Klarna row all render as sibling blocks to variant-picker inside
      // .product-details, not inside variant-picker itself, so they must be rescued
      // separately or they're lost when .product-details is removed below.
      const colorSwatchNav = productGrid.querySelector('[data-color-swatch-nav]');
      const productDisclosures = productGrid.querySelector('.product-disclosures');
      const klarnaRow = productGrid.querySelector('.product-klarna-row');
      // From the fetched page, so it follows a colour swap.
      const pageTitle = productGrid.querySelector('.view-product-title a')?.textContent?.trim();
      const productTitle = document.createElement('a');
      productTitle.textContent = pageTitle || this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = productUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (colorSwatchNav) {
        productGrid.appendChild(colorSwatchNav);
      }
      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productDisclosures) {
        productGrid.appendChild(productDisclosures);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (klarnaRow) {
        productGrid.appendChild(klarnaRow);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/**
 * Opens the quick-add modal for a tile the theme didn't render (Nosto's), via a card's own path.
 * @param {{ url: string, title?: string, image?: string }} product
 */
export function openQuickAddModal({ url, title = '', image = '' }) {
  const component = /** @type {QuickAddComponent} */ (document.createElement('quick-add-component'));
  component.dataset.productUrl = url;
  component.dataset.productTitle = title;
  component.dataset.productImage = image;
  component.handleClick(new Event('click', { cancelable: true }));
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** Lets Klarna's "Learn more" popup paint over the modal. */
  #klarnaPopupYield = new KlarnaPopupYield(this, () => this.refs.dialog);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleColourSwatchClick, { signal: this.#abortController.signal });
    this.#klarnaPopupYield.connect();
    this.addEventListener(DialogCloseEvent.eventName, this.#klarnaPopupYield.reset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#klarnaPopupYield.reset);
    this.#klarnaPopupYield.disconnect();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Swaps colour in place instead of following the swatch link. Runs before
   * color-swatch-nav.js's document handler, which skips prevented events.
   * @param {MouseEvent} event
   */
  #handleColourSwatchClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    // Let modified clicks open the PDP in a new tab.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-color-swatch-nav] a.color-swatch__link');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    if (link.classList.contains('is-selected') || link.getAttribute('aria-current') === 'true') return;

    if (!activeQuickAdd) {
      window.location.href = link.href;
      return;
    }

    this.#selectSwatch(link);
    activeQuickAdd.swapColour(link.href, link);
  };

  /**
   * Selects the tapped swatch immediately, before its colour has loaded.
   * @param {HTMLAnchorElement} link
   */
  #selectSwatch(link) {
    const nav = link.closest('[data-color-swatch-nav]');
    if (!nav) return;

    for (const other of nav.querySelectorAll('a.color-swatch__link')) {
      other.classList.remove('is-selected');
      other.removeAttribute('aria-current');
    }
    link.classList.add('is-selected');
    link.setAttribute('aria-current', 'true');

    const selectedName = nav.querySelector('.color-swatch__selected-name');
    const colourName = link.getAttribute('aria-label');
    if (selectedName && colourName) selectedName.textContent = colourName;
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
